import React, { useState } from 'react';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { InternshipResult } from '../../../../types/internship';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';
import { useGoals } from '../../../../hooks/useGoals';
import { CheckCircleIcon, XCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';

interface ReflectionFormProps {
  data: Partial<InternshipResult>;
  onUpdate: (updates: Partial<InternshipResult>) => void;
  focus: CareerFocusWithMetrics;
}

const ReflectionForm: React.FC<ReflectionFormProps> = ({
  data,
  onUpdate,
  focus
}) => {
  const { darkMode } = useDarkMode();
  const { goals } = useGoals(focus.id);
  const [achievementsSummary, setAchievementsSummary] = useState(data.achievementsSummary || '');
  const [lessonsLearned, setLessonsLearned] = useState(data.lessonsLearned || '');
  const [wouldRecommend, setWouldRecommend] = useState(data.wouldRecommend ?? true);
  
  const bgColor = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const inputBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-300';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  
  const reflectionPrompts = [
    "What technical skills did you develop?",
    "What soft skills improved the most?",
    "What would you do differently?",
    "What was your proudest moment?",
    "What advice would you give to future interns?"
  ];
  
  // Update parent data whenever local state changes
  React.useEffect(() => {
    // Only update if values have actually changed from what was passed in
    const hasChanges = 
      achievementsSummary !== (data.achievementsSummary || '') ||
      lessonsLearned !== (data.lessonsLearned || '') ||
      wouldRecommend !== (data.wouldRecommend ?? true);
    
    if (hasChanges) {
      onUpdate({
        achievementsSummary,
        lessonsLearned,
        wouldRecommend
      });
    }
  }, [achievementsSummary, lessonsLearned, wouldRecommend]);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-xl font-semibold mb-2 ${textColor}`}>
          Reflect on Your Journey
        </h2>
        <p className={mutedTextColor}>
          Take a moment to reflect on your growth and learnings
        </p>
      </div>
      
      {/* Goal Achievement Summary */}
      {goals && goals.length > 0 && (
        <div>
          <label className={`block text-sm font-medium mb-3 ${textColor}`}>
            How well did you achieve your goals?
          </label>
          <div className={`space-y-2 mb-4 p-4 rounded-lg ${bgColor}`}>
            {goals.slice(0, 3).map(goal => (
              <div key={goal.id} className="flex items-center gap-2">
                <span className="text-lg">🎯</span>
                <span className={`text-sm ${textColor}`}>{goal.goal_text}</span>
              </div>
            ))}
            {goals.length > 3 && (
              <p className={`text-sm ${mutedTextColor}`}>
                +{goals.length - 3} more goals
              </p>
            )}
          </div>
          <textarea
            value={achievementsSummary}
            onChange={(e) => setAchievementsSummary(e.target.value)}
            rows={3}
            placeholder="Briefly describe your goal achievements..."
            className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500`}
          />
        </div>
      )}
      
      {/* Key Learnings */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${textColor}`}>
          Key Learnings & Takeaways
        </label>
        <div className={`mb-3 p-3 rounded-lg ${bgColor}`}>
          <p className={`text-sm font-medium mb-2 ${textColor}`}>Consider these prompts:</p>
          <ul className={`text-sm space-y-1 ${mutedTextColor}`}>
            {reflectionPrompts.map((prompt, index) => (
              <li key={index}>• {prompt}</li>
            ))}
          </ul>
        </div>
        <textarea
          value={lessonsLearned}
          onChange={(e) => setLessonsLearned(e.target.value)}
          rows={6}
          placeholder="Share your biggest learnings and how you've grown..."
          className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500`}
        />
      </div>
      
      {/* Would Recommend */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${textColor}`}>
          Would you recommend this internship to others?
        </label>
        <div className="flex items-center gap-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              value="true"
              checked={wouldRecommend === true}
              onChange={() => setWouldRecommend(true)}
              className="mr-2"
            />
            <span className={`flex items-center gap-2 ${textColor}`}>
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              Yes, I would recommend it
            </span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              value="false"
              checked={wouldRecommend === false}
              onChange={() => setWouldRecommend(false)}
              className="mr-2"
            />
            <span className={`flex items-center gap-2 ${textColor}`}>
              <XCircleIcon className="w-5 h-5 text-red-500" />
              No, I wouldn't recommend it
            </span>
          </label>
        </div>
      </div>
      
    </div>
  );
};

export default ReflectionForm;